import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Trophy, Search, ArrowDownWideNarrow, ArrowDownAZ } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Reveal } from "./Reveal";
import { fetchChampions } from "../lib/api";
import { getRankStyle, ordinal } from "../lib/rankStyle";
import { ChampionAvatar } from "./ChampionAvatar";
import { ChampionModal } from "./ChampionModal";
import { GolfProgress } from "./GolfProgress";
import { celebrateMilestone, crossedMilestone } from "../lib/celebrate";

const fmt = (n) => (n == null ? "—" : "$" + Math.round(n).toLocaleString("en-US"));
const fmtExact = (n) =>
  "$" + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtUpdated = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
};

const RankMedal = ({ rank, sizeClass = "h-9 w-9" }) => {
  const s = getRankStyle(rank);
  if (!s.badgeSvg) return null;
  return (
    <img
      src={s.badgeSvg}
      alt={`${ordinal(rank)} place`}
      className={`pointer-events-none absolute -bottom-1 -right-1 ${sizeClass} drop-shadow-[0_2px_4px_rgba(0,0,0,0.25)]`}
      data-testid={`champion-medal-${rank}`}
    />
  );
};

const FeaturedCard = ({ c, onOpen, hero }) => {
  const ranked = c.amount_raised > 0;
  const rs = getRankStyle(ranked ? c.rank : null);
  return (
    <motion.button
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: c.rank * 0.06 }}
      onClick={() => onOpen(c)}
      className="group flex flex-col items-center text-center focus:outline-none"
      data-testid={`champion-featured-${c.account_id}`}
    >
      <div className="relative transition-transform duration-300 group-hover:-translate-y-1">
        <ChampionAvatar
          champion={c}
          ringStyle={rs.ringStyle}
          sizeClass={hero ? "h-36 w-36 sm:h-40 sm:w-40" : "h-24 w-24 sm:h-28 sm:w-28"}
          textClass={hero ? "text-4xl" : "text-2xl"}
        />
        <RankMedal rank={ranked ? c.rank : null} sizeClass={hero ? "h-14 w-14" : "h-10 w-10"} />
      </div>
      <p
        className={`mt-5 font-display smallcaps leading-tight text-mds-ink ${
          hero ? "text-2xl" : "text-lg"
        }`}
      >
        {c.name}
      </p>
      <p className={`font-display ${hero ? "text-3xl" : "text-xl"}`} style={{ color: rs.color }}>
        {fmt(c.amount_raised)}{" "}
        <span className="font-sans text-[11px] uppercase tracking-[0.14em] text-mds-inksoft">Raised</span>
      </p>
    </motion.button>
  );
};

const RosterCard = ({ c, onOpen }) => {
  const ranked = c.amount_raised > 0;
  const rs = getRankStyle(ranked ? c.rank : null);
  return (
    <button
      onClick={() => onOpen(c)}
      className="group flex flex-col items-center rounded-md border border-mds-line bg-mds-white px-5 py-9 text-center transition-all duration-300 hover:-translate-y-1 hover:border-mds-gold/50 hover:shadow-md focus:outline-none"
      data-testid={`champion-roster-${c.account_id}`}
    >
      <div className="relative">
        <ChampionAvatar champion={c} ringStyle={rs.ringStyle} sizeClass="h-28 w-28" textClass="text-2xl" />
        <RankMedal rank={ranked ? c.rank : null} sizeClass="h-11 w-11" />
      </div>
      <p className="mt-5 line-clamp-2 font-display smallcaps text-xl leading-tight text-mds-ink">{c.name}</p>
      <p className="mt-1 font-display text-2xl" style={{ color: rs.color }}>
        {fmt(c.amount_raised)}{" "}
        <span className="font-sans text-[11px] uppercase tracking-[0.12em] text-mds-inksoft">Raised</span>
      </p>
    </button>
  );
};

export const Fundraising = () => {
  const [selected, setSelected] = useState(null);
  const [sortBy, setSortBy] = useState("name"); // "name" | "amount"
  const [query, setQuery] = useState("");
  const prevTotal = useRef(null);
  const [celebrating, setCelebrating] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["champions"],
    queryFn: fetchChampions,
    refetchInterval: 5 * 60 * 1000, // every 5 minutes
    refetchOnWindowFocus: true,
    staleTime: 0, // refetch on each mount / page open
  });

  const champions = useMemo(() => data?.champions || [], [data]);
  const totalRaised = data?.total_raised || 0;
  const goal = data?.overall_goal || 1000000;

  // Milestone celebration when the collective total crosses a new threshold
  useEffect(() => {
    if (!data || !totalRaised) return;
    if (prevTotal.current === null) {
      prevTotal.current = totalRaised;
      return;
    }
    if (totalRaised > prevTotal.current) {
      const hit = crossedMilestone(prevTotal.current, totalRaised);
      if (hit) {
        celebrateMilestone();
        setCelebrating(true);
        const t = setTimeout(() => setCelebrating(false), 2600);
        prevTotal.current = totalRaised;
        return () => clearTimeout(t);
      }
    }
    prevTotal.current = totalRaised;
  }, [data, totalRaised]);

  const top5 = useMemo(() => champions.filter((c) => c.amount_raised > 0 && c.rank <= 5).sort((a, b) => a.rank - b.rank), [champions]);
  const hero = top5.find((c) => c.rank === 1);
  const podium = top5.filter((c) => c.rank !== 1);

  const roster = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = champions.filter((c) => (q ? c.name.toLowerCase().includes(q) : true));
    if (sortBy === "amount") {
      list = [...list].sort((a, b) => b.amount_raised - a.amount_raised);
    } else {
      list = [...list].sort((a, b) => {
        const byLast = (a.last_name || a.name).localeCompare(b.last_name || b.name, "en", { sensitivity: "base" });
        if (byLast !== 0) return byLast;
        return (a.first_name || a.name).localeCompare(b.first_name || b.name, "en", { sensitivity: "base" });
      });
    }
    return list;
  }, [champions, sortBy, query]);

  const pct = Math.min(100, (totalRaised / goal) * 100);

  // Deep-link: open a champion's modal from ?champion=ID (e.g. shared links)
  useEffect(() => {
    if (!champions.length) return;
    const params = new URLSearchParams(window.location.search);
    const id = params.get("champion");
    if (id && !selected) {
      const match = champions.find((c) => String(c.account_id) === String(id));
      if (match) {
        setSelected(match);
        const el = document.getElementById("fundraising");
        if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 200);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [champions]);

  const handleClose = () => {
    setSelected(null);
    const params = new URLSearchParams(window.location.search);
    if (params.get("champion")) {
      params.delete("champion");
      const qs = params.toString();
      window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : "") + "#fundraising");
    }
  };

  return (
    <section id="fundraising" className="relative bg-mds-cream" data-testid="fundraising-section">
      <div className="mx-auto max-w-[1200px] px-5 py-24 sm:px-8 sm:py-32">
        <Reveal className="text-center">
          <p className="mt-2 pb-6 pt-4 font-script text-6xl leading-[1.3] text-gold sm:text-8xl">Champions of Hope</p>
          <h2 className="mt-1 font-display smallcaps text-3xl font-medium tracking-tight text-mds-ink sm:text-5xl">
            Meet our Million Dollar Men
            <br />
            &amp; Ladies of Distinction
          </h2>
        </Reveal>

        {/* Progress: golf-course tracker */}
        <Reveal delay={0.08}>
          <div className="mx-auto mt-12 max-w-4xl text-center" data-testid="fundraising-total">
            <h3 className="font-display smallcaps text-2xl font-bold text-mds-gold sm:text-3xl">
              Help Our Champions Reach the Green
            </h3>
            <p className="mx-auto mt-3 max-w-2xl font-body text-base leading-relaxed text-mds-inksoft sm:text-lg">
              Every donation moves the ball closer. Support your favorite Champion today and fuel the
              life-changing work Naples Cancer Advisors provides for our neighbors across Southwest Florida every day.{" "}
              <span className="font-bold italic">
                Select a Champion below to view their profile, make a donation, and discover how you can be part of our mission.
              </span>
            </p>
            <GolfProgress totalRaised={totalRaised} goal={goal} pct={pct} celebrating={celebrating} fmt={fmt} />
          </div>
        </Reveal>

        {isLoading || champions.length === 0 ? (
          <p className="py-16 text-center font-body text-mds-inksoft" data-testid="champions-loading">
            Gathering our champions…
          </p>
        ) : (
          <>
            <div id="champions" className="scroll-mt-28" aria-hidden="true" />
            {/* Featured top 5 */}
            {top5.length > 0 && (
              <div className="mt-6">
                {hero && (
                  <div className="flex justify-center">
                    <FeaturedCard c={hero} onOpen={setSelected} hero />
                  </div>
                )}
                {podium.length > 0 && (
                  <div className="mx-auto mt-6 grid max-w-3xl grid-cols-2 gap-x-6 gap-y-12 sm:grid-cols-4">
                    {podium.map((c) => (
                      <FeaturedCard key={c.account_id} c={c} onOpen={setSelected} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Full rankings, alphabetical by last name */}
            <div className="mt-12">
              {/* Sort + search controls */}
              <div className="mt-8 flex flex-col items-center justify-between gap-4 sm:flex-row">
                <div className="inline-flex rounded-full border border-mds-line bg-mds-white p-1" data-testid="champions-sort-toggle">
                  <button
                    onClick={() => setSortBy("name")}
                    className={`flex min-h-[44px] items-center gap-1.5 rounded-full px-4 py-2 font-sans text-[11px] uppercase tracking-[0.14em] transition-colors duration-300 lg:min-h-0 ${
                      sortBy === "name" ? "bg-mds-gold text-mds-cream" : "text-mds-inksoft hover:text-mds-ink"
                    }`}
                    data-testid="champions-sort-name"
                  >
                    <ArrowDownAZ size={14} /> Name
                  </button>
                  <button
                    onClick={() => setSortBy("amount")}
                    className={`flex min-h-[44px] items-center gap-1.5 rounded-full px-4 py-2 font-sans text-[11px] uppercase tracking-[0.14em] transition-colors duration-300 lg:min-h-0 ${
                      sortBy === "amount" ? "bg-mds-gold text-mds-cream" : "text-mds-inksoft hover:text-mds-ink"
                    }`}
                    data-testid="champions-sort-amount"
                  >
                    <ArrowDownWideNarrow size={14} /> Amount Raised
                  </button>
                </div>

                <div className="relative w-full sm:w-72">
                  <Search size={15} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-mds-inksoft" />
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search Champions…"
                    className="w-full rounded-full border border-mds-line bg-mds-white py-3 pl-11 pr-4 font-body text-base text-mds-ink placeholder:text-mds-inksoft/70 focus:border-mds-gold focus:outline-none lg:text-sm"
                    data-testid="champions-search-input"
                  />
                </div>
              </div>

              {roster.length === 0 ? (
                <p className="mt-12 text-center font-body text-mds-inksoft" data-testid="champions-no-results">
                  No champions match “{query}”.
                </p>
              ) : (
                <div
                  className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3"
                  data-testid="champions-roster"
                >
                  {roster.map((c) => (
                    <RosterCard key={c.account_id} c={c} onOpen={setSelected} />
                  ))}
                </div>
              )}
            </div>

            {data?.updated_at && (
              <p className="mt-10 text-center font-sans text-[10px] uppercase tracking-[0.16em] text-mds-inksoft/70" data-testid="champions-updated-at">
                Live totals from our fundraising platform · Last updated {fmtUpdated(data.updated_at)}
              </p>
            )}
          </>
        )}
      </div>

      <ChampionModal
        champion={selected ? champions.find((c) => c.account_id === selected.account_id) || selected : null}
        onClose={handleClose}
      />
    </section>
  );
};
